/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl.cauce

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import studio.oddpods.cauce.core.data.repository.cauce.ReminderRepository
import studio.oddpods.cauce.core.data.repository.cauce.ReminderSentEntry
import studio.oddpods.cauce.core.data.util.safeCall
import studio.oddpods.cauce.core.data.util.safeThrow
import studio.oddpods.cauce.core.model.reminder.PendingReminder

class ReminderRepositoryImpl(
    private val supabase: SupabaseClient,
) : ReminderRepository {

    private val pending = MutableStateFlow<List<PendingReminder>?>(null)

    override fun getPendingReminders(): Flow<List<PendingReminder>> =
        pending
            .onStart { if (pending.value == null) fetch() }
            .filterNotNull()

    override fun getPendingCount(): Flow<Int> =
        getPendingReminders().map { list -> list.sumOf { it.dueSessions.size } }

    override suspend fun refresh(): Result<Unit> = safeCall {
        fetch()
    }

    override suspend fun recordBatchSent(entries: List<ReminderSentEntry>): Result<Int> = safeCall {
        val payload = JsonArray(
            entries.map { e ->
                buildJsonObject {
                    put("session_id", e.sessionId)
                    put("reminder_number", e.reminderNumber)
                }
            },
        )
        val result = supabase.postgrest.rpc(
            "record_reminder_sent",
            buildJsonObject { put("p_entries", payload) },
        )
        val inserted = result.decodeAs<Int>()
        fetch()
        inserted
    }

    private suspend fun fetch() = safeThrow {
        val result = supabase.postgrest.rpc("get_pending_reminders")
        pending.value = result.decodeAs<List<PendingReminder>>()
    }
}
